import { Component, OnInit, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { sortBy } from "lodash";
import { NgxSpinnerModule, NgxSpinnerService } from "ngx-spinner";
import { firstValueFrom } from "rxjs";
import { SessionFacility } from "src/app/common/interfaces/facility-with-timezone";
import { FacilityListItem } from "src/app/common/interfaces/facility.interface";
import { FacilitiesClient } from "src/app/common/services/facility.client";
import { SecurityGroupClient } from "src/app/common/services/security-group.client";
import { StorageService } from "src/app/common/services/storage.service";
import { UserEsignatureClient } from "src/app/common/services/user-esignature.client";
import { UserClient } from "src/app/common/services/user.client";
import { AuthService } from "src/app/services/auth.service";
import { EsignatureAttestationComponent } from "../login/esignature-attestation/esignature-attestation.component";
import { CredentialsUpdate } from "src/app/common/interfaces/credentials-updates.interface";
import { CredentialUpdatesComponent } from "../login/credential-updates/credential-updates.component";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { ConfirmIdentityComponent } from "../practitioner-setup/confirm-identity.component";
import { environment } from "environment";
import { AppConstants } from "src/app/common/constants/app-constants";
import { appRoute } from "src/app/app-route.constants";
import { User } from "src/app/common/interfaces/user.interface";
@Component({
  selector: "app-temp-home",
  standalone: true,
  imports: [NgxSpinnerModule],
  providers: [],
  template: ` <ngx-spinner bdColor="rgba(255,255,255,0.7)" size="medium" color="#4663AE" type="square-jelly-box" [fullScreen]="true" [disableAnimation]="false"></ngx-spinner> `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        position: relative;
      }
    `,
  ],
})
export class ValidateComponent implements OnInit {
  private _spinner = inject(NgxSpinnerService);
  private _router = inject(Router);
  private _storageService = inject(StorageService);
  private _facilitiesClient = inject(FacilitiesClient);
  private _userClient = inject(UserClient);
  private _activatedRoute = inject(ActivatedRoute);
  private _securityGroupClient = inject(SecurityGroupClient);
  private _esignatureClient = inject(UserEsignatureClient);
  private _matDialog = inject(MatDialog);
  private _authService = inject(AuthService);
  async ngOnInit() {
    this._spinner.show();
    try {
      const code = this._activatedRoute.snapshot.queryParamMap.get("code");
      if (!code) {
        this._router.navigate(["home"]);
        return;
      }
      let response;
      try {
        response = await firstValueFrom(this._authService.getTokenFromAuthorizationCode(code!));
        this._storageService.setSessionActive();
      } catch (err) {
        this._router.navigate(["home"]);
        return;
      }
      this._storageService.setLoginData(response, true);
      
      let isUserActive = await firstValueFrom(this._authService.IsUserActive());
      let userDetails = await firstValueFrom(this._authService.getUserInfo());
      if (!isUserActive || (!userDetails.issuperuser && userDetails.status !== AppConstants.enabled)) {
        const message = "You do not have enough privileges to access this application.Please contact your administrator or write to support@z-pax.com for assistance.";
        const title = "Access Denied";

        const val = await firstValueFrom(
          this._matDialog
            .open(ConfirmationModalComponent, {
              data: {
                title: title,
                message: message,
                buttonText: "OK",
                hideCancelButton: true,
                isError: true,
              },
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
        );

        this._authService.logout();
        return;
      }
      userDetails = {
        ...userDetails,
        fullname: userDetails.lastname + ", " + userDetails.firstname,
      };
      this._storageService.setUser(userDetails);
      this.setUserIdToMatomo(userDetails);
      if (userDetails.issuperuser) {
        if (!!userDetails.latestSelectedCorpId && !!userDetails.latestSelectedFacilityId) {
          this._storageService.setSelectedFacilityId(userDetails.latestSelectedFacilityId);
          this._storageService.setSelectedCorporationId(userDetails.latestSelectedCorpId);
        }
      }

      const eSignatureRequired = await firstValueFrom(this._esignatureClient.isEsignatureRequired());
      const isEsignatureRequired: boolean = eSignatureRequired.isEsignatureRequired;
      const isEsignEnabled: boolean = eSignatureRequired.isEsignEnabled;

      const isPhysician: boolean = eSignatureRequired.isPhysician;

      const securityGroupId = JSON.parse(this._storageService.getUser()!).defaultsecuritygroupid;

      const securityGroup = await firstValueFrom(this._securityGroupClient.getSecurityGroup(securityGroupId));

      const user = JSON.parse(this._storageService.getUser()!);
      if (user.npi) {
        if (!isEsignEnabled) {
          const message = "Your eSignature is yet to be authorised please contact Admin or wait for sometime.";
          const title = "Access Denied";

          const val = await firstValueFrom(
            this._matDialog
              .open(ConfirmationModalComponent, {
                data: {
                  title: title,
                  message: message,
                  buttonText: "OK",
                  hideCancelButton: true,
                  isError: true,
                },
                disableClose: true,
                autoFocus: false,
              })
              .afterClosed()
          );

          this._authService.logout();
          return;
        }
        await firstValueFrom(
          this._matDialog
            .open(ConfirmIdentityComponent, {
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
        );
      }

      if (eSignatureRequired && securityGroup && securityGroup.name && isEsignatureRequired) {
        await firstValueFrom(
          this._matDialog
            .open(EsignatureAttestationComponent, {
              disableClose: true,
              autoFocus: false,
              data: {
                isPhysician: isPhysician,
              },
            })
            .afterClosed()
        );
      }

      if (user?.npi) {
        const npiChecks = (await firstValueFrom(this._userClient.userNPICheck(user.npi))) as CredentialsUpdate;

        if (npiChecks.differences.length > 0 && npiChecks.isUpdateFound && !npiChecks?.isNpiDeactivated) {
          const valid = await firstValueFrom(
            this._matDialog
              .open(CredentialUpdatesComponent, {
                disableClose: true,
                autoFocus: false,
                data: {
                  updates: npiChecks,
                },
              })
              .afterClosed()
          );

          if (!valid) {
            // const message = await firstValueFrom(
            //   this._esignatureClient.declineNPIData()
            // );
            // has removed message coming form backend due to message not being correct
            const message = "Please be advised that your record is not matching on our NPI records. Do contact the administrator for resolution.";
            await firstValueFrom(
              this._matDialog
                .open(ConfirmationModalComponent, {
                  data: {
                    title: "Error",
                    message: message,
                    buttonText: "OK",
                    hideCancelButton: true,
                    isError: true,
                  },
                })
                .afterClosed()
            );
            this._authService.logout();
            return;
          } else {
            sessionStorage.setItem("last-active-url", "/user/" + user.id + "/account");
            sessionStorage.setItem("last-active-user-id", user.id);
            await firstValueFrom(this._esignatureClient.updateNPIData());
            sessionStorage.clear();
            sessionStorage.clear();
            window.location.href = `${environment.zpaxEsignatureDocumentRedirectEndPoint}`;
            return;
          }
        } else if (npiChecks?.isNpiDeactivated) {
          const message = "Please be advised that your record is not available on our NPI records. Do contact the administrator for resolution.";
          await firstValueFrom(
            this._matDialog
              .open(ConfirmationModalComponent, {
                data: {
                  title: "Error",
                  message: message,
                  buttonText: "OK",
                  hideCancelButton: true,
                  isError: true,
                },
              })
              .afterClosed()
          );
          this._authService.logout();
          return;
        } else {
          //valid user redirect to zpax
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = `${environment.zpaxEsignatureDocumentRedirectEndPoint}`;
          return;
        }
      }
      this._storageService.setSelectedCorporationId(userDetails.latestSelectedCorpId ?? userDetails.defaultcorporationid);
      await this.populateFacilityList();
      const userFacilities = await firstValueFrom(this._userClient.getUserFacilities(user.id));

      const defaultFacilityIdFromList = userFacilities.find((c) => c.isdefaultfacility)?.facilityid;

      const facilityId = defaultFacilityIdFromList ?? (userFacilities.length > 0 ? userFacilities[0].facilityid : null);

      if (facilityId) {
        this._storageService.setSelectedFacilityId(facilityId);

        this._storageService.setSecurityFeatureData(await firstValueFrom(this._securityGroupClient.getFeaturePermission(facilityId)));
      } else {
        this._storageService.setSecurityFeatureData(await firstValueFrom(this._securityGroupClient.getFeaturePermission()));
      }
      this._spinner.hide();
      const lastActiveUserId = this._storageService.getLastActiveUserId();
      if(lastActiveUserId !== user.id){
        this._storageService.clearSessionState();
      }
      const lastActiveUrl =  this._storageService.getLastActiveUrl();
      let activePath =`/${appRoute.home}`;
      if(!!lastActiveUrl && !lastActiveUrl.includes(appRoute.validate)){
        activePath = lastActiveUrl;
      }
      this._router.navigateByUrl(activePath, { replaceUrl: true }).then(() => {
        this._storageService.setLoggedIn();
      });
    } catch (err: unknown) {
      this._spinner.hide();
      if ((err as { error: string })?.error.startsWith("The account associated with User ID")) {
      }
    }
  }
  async populateFacilityList() {
    const fullFacilities = (await firstValueFrom(this._facilitiesClient.getFacilities({ length: 500000 }))).data;

    const facilitiesObject: SessionFacility[] = [];

    fullFacilities.forEach((facility: FacilityListItem) => {
      facilitiesObject.push({
        name: facility.facilityname,
        facilityId: facility.id,
        timezone: facility.timezonename || "America/New_York",
        providerNumber: facility.providerno,
      });
    });

    this._storageService.setFacilities(sortBy(facilitiesObject, "name"));
  }

  private setUserIdToMatomo(userDetails: User) {
    const userName = `${userDetails.fullname} - ${userDetails.id}`;
    if ((window as any)._paq) {
      (window as any)._paq.push(['setUserId', userName]);

      (window as any)._paq.push([
        'trackEvent',
        'Log In',
        'User Login',
        `${userName}`
      ]);
    }
  }
}
